import type { ClaudeSession, ConversationTurn } from '@/features/events/conversation/state/types'
import { AgentMode } from '../types/enums'
import type { UUID } from '@/common/types'

export interface AgentModeTransition {
  readonly from: AgentMode
  readonly to: AgentMode
}

export interface AgentModeState {
  readonly currentMode: AgentMode
  readonly pendingTransition: AgentModeTransition | null
}

export function resolveSessionAgentMode(session: ClaudeSession): AgentMode {
  return session.runtime.pendingAgentMode ?? session.currentAgentMode
}

export function deriveAgentModeState(
  currentTurn: ConversationTurn | null | undefined,
  _previousTurn: ConversationTurn | null | undefined,
  defaultMode: AgentMode,
): AgentModeState {
  if (!currentTurn) {
    return {
      currentMode: defaultMode,
      pendingTransition: null,
    }
  }

  const latestCommand = currentTurn.userCommands.at(-1)
  const pendingMode = latestCommand?.agentMode ?? null
  const currentMode = currentTurn.events.at(-1)?.agentMode ?? defaultMode

  if (pendingMode !== null && pendingMode !== currentMode) {
    return {
      currentMode: pendingMode,
      pendingTransition: { from: currentMode, to: pendingMode },
    }
  }

  return {
    currentMode,
    pendingTransition: null,
  }
}

export function getPreviousConversationTurn(
  session: ClaudeSession,
  currentUserPromptId: UUID,
): ConversationTurn | null {
  const currentTurn = session.conversationTurns.get(currentUserPromptId)
  if (!currentTurn) {
    return null
  }

  const allTurns = Array.from(session.conversationTurns.values()).sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  )

  const currentIndex = allTurns.findIndex((turn) => turn.userPromptId === currentUserPromptId)

  return currentIndex > 0 ? (allTurns[currentIndex - 1] ?? null) : null
}

export function deriveSessionAgentModeState(session: ClaudeSession): AgentModeState {
  const pending = session.runtime.pendingAgentMode
  const current = session.currentAgentMode

  if (pending !== null && pending !== current) {
    return {
      currentMode: pending,
      pendingTransition: { from: current, to: pending },
    }
  }

  return {
    currentMode: current,
    pendingTransition: null,
  }
}
